import { type ReactNode } from 'react'
import { Outlet, useLoaderData, type LoaderFunctionArgs } from 'react-router'
import { AnalyticsBootstrap } from '#app/components/analytics/AnalyticsBootstrap'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { SMkFooterMenu } from '#app/components/navigation/Footer.tsx'
import Navbar from '#app/components/navigation/NavBar.tsx'
import { NavigationRoutes, NavigationPaths } from '#app/config/index.server.ts'
import { FeatureFlagsProvider } from '#app/contexts/FeatureFlagsContext.tsx'
import { PermissionsProvider } from '#app/contexts/PermissionsContext'
import { getCategoriesWithCourseCountCached } from '#app/domains/categories'
import { getPublicCollectionsCached } from '#app/domains/courses'
import { FEATURED_ORDER_BY } from '#app/domains/courses/application/effects/courseSortOrder'
import {
	createEffectErrorHandler,
	createErrorLogger,
} from '#app/domains/shared/handlers/effectErrorBoundary.server'
import { getImperativeLogger } from '#app/domains/shared/infrastructure/loggerRuntime'
import { errorToResponse } from '#app/domains/shared/services/errorMapper.server'
import { runWithDI } from '#app/utils/di/withDI'
import { pipeHeaders } from '#app/utils/headers.server'
import { getMarketingShellBootstrap } from '#app/utils/pz/bootstrap/shellBootstrap.server'
import { getResolvedLeadMagnet } from '#app/utils/pz/leadMagnet/leadMagnet.server'
import { getMarketingSignupHrefOverride } from '#app/utils/pz/marketing/acquisitionPolicy'
import { type CourseError } from '../domains/courses'

// ============================================================================
// Error Handler Configuration
// ============================================================================

const marketingLogger = createErrorLogger('MarketingLayout')
const marketingRuntimeLogger = getImperativeLogger('MarketingLayout')
const MARKETING_SHELL_TIMEOUT_MS = 2500

type MarketingShellLoaderData = {
	permissions: unknown
	featureFlags: Record<string, unknown>
	navigationRoutes: typeof NavigationRoutes
	navigationPaths: typeof NavigationPaths
	consentGiven: boolean
	categories: unknown[]
	collections: unknown[]
	signupHrefOverride: string | null
}

const marketingErrorHandler = createEffectErrorHandler({
	errorMapper: errorToResponse,
	logger: marketingLogger,
})

function createMarketingShellFallback(): MarketingShellLoaderData {
	return {
		permissions: {},
		featureFlags: {},
		navigationRoutes: NavigationRoutes,
		navigationPaths: NavigationPaths,
		consentGiven: false,
		categories: [],
		collections: [],
		signupHrefOverride: null,
	}
}

async function withMarketingShellTimeout(
	promise: Promise<MarketingShellLoaderData>,
	brandKey: string,
): Promise<MarketingShellLoaderData> {
	let timeoutId: ReturnType<typeof setTimeout> | undefined

	try {
		return await Promise.race([
			promise,
			new Promise<MarketingShellLoaderData>((resolve) => {
				timeoutId = setTimeout(() => {
					marketingRuntimeLogger.warn('Marketing shell timed out', {
						brandKey,
						timeoutMs: MARKETING_SHELL_TIMEOUT_MS,
					})
					resolve(createMarketingShellFallback())
				}, MARKETING_SHELL_TIMEOUT_MS)
			}),
		])
	} catch (error) {
		marketingRuntimeLogger.warn('Marketing shell failed', {
			brandKey,
			error: error instanceof Error ? error.message : String(error),
		})
		return createMarketingShellFallback()
	} finally {
		if (timeoutId) clearTimeout(timeoutId)
	}
}

export async function loader({ request }: LoaderFunctionArgs) {
	try {
		const { resolveBrandConfig } = await import(
			'#app/config/brands/brands.config'
		)
		const { key: brandKey } = resolveBrandConfig()
		const shell = await withMarketingShellTimeout(
			getMarketingShellBootstrap(request, {
				navigationRoutes: NavigationRoutes,
				navigationPaths: NavigationPaths,
				loadExtras: async () => {
					const leadMagnet = await getResolvedLeadMagnet()
					const [categories, collections] = await Promise.all([
						runWithDI(getCategoriesWithCourseCountCached()),
						runWithDI(
							getPublicCollectionsCached({
								take: 10,
								// Canonical featured-first order shared with the other
								// program surfaces (was updatedAt desc).
								orderBy: FEATURED_ORDER_BY,
							}),
						),
					])

					return {
						categories,
						collections,
						signupHrefOverride: getMarketingSignupHrefOverride({
							brandKey,
							leadMagnetSignupPath: leadMagnet.signupPath,
						}),
					}
				},
			}) as Promise<MarketingShellLoaderData>,
			brandKey,
		)
		return {
			permissions: shell.permissions,
			featureFlags: shell.featureFlags,
			navRoutes: shell.navigationRoutes,
			navPaths: shell.navigationPaths,
			consentGiven: shell.consentGiven,
			categories: shell.categories,
			collections: shell.collections,
			signupHrefOverride: shell.signupHrefOverride,
		}
	} catch (error) {
		throw marketingErrorHandler(error as CourseError)
	}
}

export function MarketingShell({ children }: { children: ReactNode }) {
	const data = useLoaderData() as any
	return (
		<FeatureFlagsProvider flags={data?.featureFlags ?? {}}>
			<PermissionsProvider permissions={data?.permissions ?? {}}>
				<div className="min-h-screen">
					<Navbar
						navRoutes={data?.navRoutes}
						navPaths={data?.navPaths}
						categories={data?.categories}
						signupHrefOverride={data?.signupHrefOverride}
					/>
					{children}
					<SMkFooterMenu
						navRoutes={data?.navRoutes}
						navPaths={data?.navPaths}
						categories={data?.categories}
						collections={data?.collections}
						signupHrefOverride={data?.signupHrefOverride}
					/>
					<AnalyticsBootstrap
						initialConsent={Boolean(data?.consentGiven)}
						pageType="marketing"
						showCookieBanner
					/>
				</div>
			</PermissionsProvider>
		</FeatureFlagsProvider>
	)
}

export default function MarketingLayout() {
	return (
		<MarketingShell>
			<Outlet />
		</MarketingShell>
	)
}

export const headers = pipeHeaders

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
